/* global globalThis */
/**
 * Stale gallery image investigation (editor → gallery).
 *
 * UploadFloor.js and LayoutVersionSlider.js assign `const console = { log(){}… }`
 * so normal `console.log` is a no-op inside those modules. These helpers always
 * emit via `globalThis.console` so logs are visible in DevTools.
 *
 * Filter DevTools console by: [MF:stale-gallery]
 * Guest Done → auth → gallery: [MF:guest-gallery-auth]
 * Raster pipeline (downloadLayout type=2): [MF:gallery-capture-diag]
 * Guest auth gate / Save vs fork: authGate.*, forkDiag.authNonForkPath, guestFork.forkOnEdit
 *
 * These emitters are OFF by default so they don't flood the console (or leak
 * internal diagnostics) in staging/production. Re-enable on demand without a
 * rebuild via any one of:
 *   - localStorage.setItem("mf:gallery-debug", "1")  // persists across reloads
 *   - add ?mfGalleryDebug=1 to the URL
 *   - set globalThis.__MF_GALLERY_DEBUG__ = true      // live toggle in console
 */

/** @type {boolean | undefined} */
let mfGalleryDebugCache;

/**
 * Whether the [MF:*] gallery diagnostic emitters should log. Default false.
 * The localStorage/URL result is cached (read once) to keep the hot render
 * path cheap; the runtime global is checked live so it can be flipped on in
 * the console mid-session.
 */
export function mfGalleryDebugEnabled() {
  try {
    if (globalThis.__MF_GALLERY_DEBUG__ === true) return true;
    if (globalThis.__MF_GALLERY_DEBUG__ === false) return false;
  } catch {
    /* ignore */
  }
  if (mfGalleryDebugCache !== undefined) return mfGalleryDebugCache;
  let enabled = false;
  try {
    if (typeof window !== "undefined") {
      const ls = window.localStorage?.getItem("mf:gallery-debug");
      if (ls === "1" || ls === "true") {
        enabled = true;
      } else if (/[?&]mfGalleryDebug=1(?:&|$)/.test(window.location?.search || "")) {
        enabled = true;
      }
    }
  } catch {
    enabled = false;
  }
  mfGalleryDebugCache = enabled;
  return enabled;
}

/**
 * Lightweight fingerprint of rooms payload for comparing snapshot vs live refs vs PUT.
 * @param {unknown} roomsData
 */
export function fingerprintGuestLayoutState(roomsData) {
  if (!Array.isArray(roomsData) || roomsData.length === 0) {
    return { roomCount: 0, furnitureCount: 0, firstFurniture: null };
  }
  let furnitureCount = 0;
  /** @type {Record<string, unknown> | null} */
  let firstFurniture = null;
  for (const room of roomsData) {
    for (const layout of room?.layouts || []) {
      for (const f of layout?.furniture || []) {
        furnitureCount += 1;
        if (!firstFurniture && f) {
          firstFurniture = {
            name: f.name ?? null,
            id: f._id ?? null,
            exactPosition: f.exactPosition ?? null,
            position: f.position ?? null,
            width: f.width ?? null,
          };
        }
      }
    }
  }
  return {
    roomCount: roomsData.length,
    furnitureCount,
    firstFurniture,
  };
}

/** DOM furniture nodes visible to downloadLayout at capture time. */
export function fingerprintEditorDomCapture() {
  if (typeof document === "undefined") {
    return { boundaryCount: null, imageContainerPresent: false };
  }
  const container = document.querySelector(".image-container");
  const boundaries = container?.querySelectorAll(".image-boundary");
  return {
    imageContainerPresent: !!container,
    boundaryCount: boundaries?.length ?? 0,
  };
}

/**
 * Rich DOM + navigation context for guest fork vs Done capture comparison.
 * Safe to call from async boundaries (no React state reads here).
 */
export function fingerprintGuestForkDomDeep() {
  if (typeof document === "undefined") {
    return { ok: false, reason: "no_document" };
  }
  const path = typeof window !== "undefined" ? window.location?.pathname ?? "" : "";
  const el = document.querySelector(".image-container");
  if (!el) {
    return {
      path,
      visibility: document.visibilityState,
      hidden: document.hidden,
      imageContainerPresent: false,
    };
  }
  const r = el.getBoundingClientRect();
  const imgs = el.querySelectorAll("img");
  let imgLoadedOk = 0;
  let imgIncomplete = 0;
  imgs.forEach((img) => {
    if (img.complete && img.naturalHeight > 0) imgLoadedOk += 1;
    else imgIncomplete += 1;
  });
  let transform = null;
  try {
    transform = window.getComputedStyle(el).transform;
  } catch {
    transform = "compute_error";
  }
  const tStr = transform && transform !== "none" ? String(transform).slice(0, 96) : "none";
  return {
    path,
    visibility: document.visibilityState,
    hidden: document.hidden,
    imageContainerPresent: true,
    rect: { w: Math.round(r.width), h: Math.round(r.height) },
    transform: tStr,
    boundaryCount: el.querySelectorAll(".image-boundary").length,
    svgImageCount: el.querySelectorAll("img.svg-image").length,
    imgTotal: imgs.length,
    imgLoadedOk,
    imgIncomplete,
    childElementCount: el.childElementCount,
  };
}

/**
 * Internal gallery raster pipeline (downloadLayout type === 2).
 * Filter: [MF:gallery-capture-diag]
 * @param {string} phase
 * @param {Record<string, unknown>} [data]
 */
export function mfLogGalleryCaptureDiag(phase, data) {
  if (!mfGalleryDebugEnabled()) return;
  const c = globalThis.console;
  if (!c?.log) return;
  const payload =
    data && typeof data === "object" && !Array.isArray(data)
      ? { phase, ts: new Date().toISOString(), ...data }
      : { phase, ts: new Date().toISOString(), data };
  c.log("[MF:gallery-capture-diag]", payload);
}

/**
 * Guest fork: Done → auth modal → saveForkedLayoutAndNavigate → gallery.
 * @param {string} phase
 * @param {Record<string, unknown>} [data]
 */
export function mfLogGuestGalleryAuth(phase, data) {
  if (!mfGalleryDebugEnabled()) return;
  const c = globalThis.console;
  if (!c?.log) return;
  const payload =
    data && typeof data === "object" && !Array.isArray(data)
      ? { phase, ts: new Date().toISOString(), ...data }
      : { phase, ts: new Date().toISOString(), data };
  c.log("[MF:guest-gallery-auth]", payload);
}

/** @param {unknown} url */
export function summarizeImageUrl(url) {
  if (url == null || url === "") return null;
  const s = String(url);
  if (s.startsWith("blob:")) {
    return { kind: "blob", len: s.length, tail: s.slice(-24) };
  }
  return { kind: "http", len: s.length, tail: s.slice(-56) };
}

/**
 * @param {string} phase Short stable label for filtering / grouping.
 * @param {Record<string, unknown>} [data] Serializable fields only.
 */
export function mfLogGalleryStale(phase, data) {
  if (!mfGalleryDebugEnabled()) return;
  const c = globalThis.console;
  if (!c?.log) return;
  const payload =
    data && typeof data === "object" && !Array.isArray(data)
      ? { phase, ts: new Date().toISOString(), ...data }
      : { phase, ts: new Date().toISOString(), data };
  c.log("[MF:stale-gallery]", payload);
}
